/**
 * AIF-05 — pure model helpers for the AI Mapping tab (no React), unit-tested in
 * isolation:
 *  - `entryBreadcrumb` — the full group → leaf path for a catalogue entry, so
 *    same-named entries under different groups are distinguishable (AC2).
 *  - `inheritedSeverity` — resolve a type entry's per-class-model default
 *    severity (the inherited value a row shows before any override) (AC4).
 *  - `coverage` — n/N mapped + per-filter row visibility for the model
 *    switcher's coverage indicator.
 */
import type {
  AiClassMapping,
  AiFindingModelClass,
  AiMapValidationError,
  AiMapValidationIssue,
  DefectCatalogueEntryV2,
  LocalizedString,
} from "@aeriosiq/types";
import { locText } from "./locText.js";

interface AxiosLikeError {
  status?: number;
  cause?: { response?: { status?: number; data?: unknown } };
}

/**
 * AIF-05 — pull the per-row 422 `AiMapValidationError` issues off an axios-like
 * error (the api-client rethrows with the original axios error on `.cause`).
 * Returns `null` when the error is not a 422 ai-map validation envelope.
 */
export function extractAiMapIssues(
  err: unknown,
): AiMapValidationIssue[] | null {
  const e = err as AxiosLikeError;
  const status = e.status ?? e.cause?.response?.status;
  if (status !== 422) return null;
  const data = e.cause?.response?.data as
    | Partial<AiMapValidationError>
    | undefined;
  if (data?.code !== "validation_error" || !Array.isArray(data.issues)) {
    return null;
  }
  return data.issues;
}

/** entryId → entry index for one axis. */
export function indexEntries(
  entries: DefectCatalogueEntryV2[],
): Map<string, DefectCatalogueEntryV2> {
  const m = new Map<string, DefectCatalogueEntryV2>();
  for (const e of entries) m.set(e.entryId, e);
  return m;
}

/**
 * The ordered breadcrumb path of localized names from the entry's axis root down
 * to the entry itself (e.g. ["Surface", "Erosion", "LE erosion"]). Cycle-safe.
 * An unknown id yields an empty array.
 */
export function entryBreadcrumb(
  entryId: string | null | undefined,
  byId: ReadonlyMap<string, DefectCatalogueEntryV2>,
): string[] {
  if (entryId == null || entryId.length === 0) return [];
  const chain: DefectCatalogueEntryV2[] = [];
  const seen = new Set<string>();
  let cur: DefectCatalogueEntryV2 | undefined = byId.get(entryId);
  while (cur && !seen.has(cur.entryId)) {
    seen.add(cur.entryId);
    chain.push(cur);
    const pid: string | null | undefined = cur.parentEntryId;
    cur = pid != null && pid.length > 0 ? byId.get(pid) : undefined;
  }
  chain.reverse();
  return chain.map((e) => locText(e.name));
}

/** Render a breadcrumb as a single " › "-joined string. */
export function breadcrumbText(path: string[]): string {
  return path.join(" › ");
}

/**
 * The inherited default severity (defect_class_id) for a type entry under a
 * given class model, or null when the entry has none. This is the value a row
 * displays as "inherited" before any per-AI-class override.
 */
export function inheritedSeverity(
  entry: DefectCatalogueEntryV2 | undefined,
  classModelId: string | null,
): string | null {
  if (!entry || classModelId === null) return null;
  const map = entry.defaultSeverityByModel;
  if (!map) return null;
  const v = map[classModelId];
  return v != null && v.length > 0 ? v : null;
}

export type CoverageFilter = "all" | "mapped" | "unmapped";

export interface ClassRow {
  cls: AiFindingModelClass;
  mapping: AiClassMapping | undefined;
}

/**
 * Build the table rows for one model: every class of the model joined with its
 * mapping (if any), filtered by the all/mapped/unmapped selector. `mappings` is
 * the WHOLE version map (all models); only rows whose classUuid belongs to this
 * model's classes are joined. Rows are ordered by classIndex.
 */
export function buildClassRows(
  classes: AiFindingModelClass[],
  mappings: AiClassMapping[],
  filter: CoverageFilter,
): ClassRow[] {
  const byClassUuid = new Map<string, AiClassMapping>();
  for (const m of mappings) byClassUuid.set(m.classUuid, m);
  const rows: ClassRow[] = [...classes]
    .sort((a, b) => a.classIndex - b.classIndex)
    .map((cls) => ({ cls, mapping: byClassUuid.get(cls.classUuid) }));
  if (filter === "mapped") return rows.filter((r) => r.mapping !== undefined);
  if (filter === "unmapped") return rows.filter((r) => r.mapping === undefined);
  return rows;
}

export interface Coverage {
  mapped: number;
  total: number;
}

/** n mapped / N classes for one model (against the whole version map). */
export function coverage(
  classes: AiFindingModelClass[],
  mappings: AiClassMapping[],
): Coverage {
  const mappedUuids = new Set(mappings.map((m) => m.classUuid));
  const total = classes.length;
  const mapped = classes.reduce(
    (n, c) => (mappedUuids.has(c.classUuid) ? n + 1 : n),
    0,
  );
  return { mapped, total };
}

/** Render a class label as `#index · className` for the table + dropdown. */
export function classLabel(cls: AiFindingModelClass): string {
  return `#${String(cls.classIndex)} · ${cls.className}`;
}

/** Render a class's display name (i18n) when present, else null. */
export function classDisplayName(
  cls: Pick<AiFindingModelClass, "displayName">,
): string | null {
  const dn: LocalizedString | null | undefined = cls.displayName;
  if (!dn) return null;
  const t = locText(dn, "");
  return t.length > 0 ? t : null;
}
